// Packages
import {
  Box,
  IconButton,
  Typography,
  Divider
} from "@mui/material";

// Components
import StatusTag from "components/StatusTag";
import CheckboxColumn from "components/Table/CheckboxColumn";

// Icon
import { ReactComponent as PlayIcon } from "assets/images/icons/icn_btn_start.svg";
import { ReactComponent as PauseIcon } from "assets/images/icons/btn_Pause.svg";
import { ReactComponent as EditIcon } from "assets/images/icons/btn_edit.svg";
import { ReactComponent as DeleteIcon } from "assets/images/icons/icn_table_cell_Bin.svg";

// const
import { statusMappingDefinition, labelMappingDefinition } from './main';

export const getMainTableColumns = ({
  auth,
  columnHelper,
  permissions,
  theme,
  mainTableCallbacks
}) => {

  const {
    onPlay,
    onPause,
    onEdit,
    onRemove
  } = mainTableCallbacks;

  return [
    columnHelper.accessor("formattedActiveState", {
      id: "formattedActiveState",
      header: "Status",
      cell: info => {
        const activeState = info.row.original.activeState;

        return (
          <StatusTag
            status={activeState}
            statusMapping={statusMappingDefinition}
            labelMapping={labelMappingDefinition}
          />
        )
      },
    }),
    columnHelper.accessor(row => row.formattedModelName ?? "-", {
      id: "formattedModelName",
      header: "Model Name",
      cell: info => <>{info.getValue()}</>,
    }),
    columnHelper.accessor(row => row.formattedHwVerLabel ?? "-", {
      id: "formattedHwVerLabel",
      header: "Hardware Ver.",
      cell: info => <>{info.getValue()}</>,
    }),
    columnHelper.accessor(row => row.formattedPortCount ?? "-", {
      id: "formattedPortCount",
      header: "Ports",
      cell: info => <>{info.getValue()}</>,
    }),
    columnHelper.display({
      id: "action",
      header: "Action",
      cell: info => {

        const selectedConfig = info.row.original;
        const activeState = !!selectedConfig?.activeState;

        return (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.3 }}>

            {
              activeState ?
                <IconButton onClick={() => onPause(selectedConfig)}><PauseIcon /></IconButton> :
                <IconButton onClick={() => onPlay(selectedConfig)}><PlayIcon /></IconButton>
            }

            <Divider orientation="vertical" flexItem sx={{ height: "20px", alignSelf: "center", margin: "2px" }} />

            <IconButton onClick={() => onEdit(selectedConfig)}><EditIcon /></IconButton>

            <Divider orientation="vertical" flexItem sx={{ height: "20px", alignSelf: "center", margin: "2px" }} />

            <IconButton onClick={() => onRemove(selectedConfig)}><DeleteIcon /></IconButton>

          </Box>
        )
      },
    }),
  ].filter(Boolean);
}

export const getPortsTableColumns = ({
  auth,
  columnHelper,
  permissions,
  theme,
  scheduleProfiles = [],
  portProfiles = []
}) => {
  // portProfile stores the profile id, so resolve it to the profile name for display
  const getPortProfileName = (row) => {
    const matchedProfile = portProfiles.find(profile => profile.id === row.portProfile);
    return row.selectedProfile?.name || matchedProfile?.name || row.portProfileName || row.portProfile;
  };

  return [
    permissions && (permissions === "Admin" || permissions === "Super Admin") && CheckboxColumn(columnHelper, null, {}),
    columnHelper.accessor(row => row.modelName, {
      id: "modelName",
      header: "Model Name",
      cell: info => <>{info.getValue()}</>,
    }),
    columnHelper.accessor(row => row.portIndex ?? "-", {
      id: "portIndex",
      header: "Port",
      cell: info => {
        const origin = info.row.original;

        if(origin.isNeedCombineModel) return <>{origin.combinePortIndex}</>
        else return <>{info.getValue()}</>
      },
    }),
    columnHelper.accessor(row => {
      if (row?.isNeedCombineModel) return row?.combinePortName || "-";
      return row?.portName || "-";
    }, {
      id: "portName",
      header: "Port Name",
      cell: info => {
        const origin = info.row.original;
        const val = origin.isNeedCombineModel ? origin.combinePortName : origin.portName;
        return <Box>{val || "-"}</Box>;
      },
    }),
    columnHelper.accessor(row => getPortProfileName(row), {
      id: "portProfile",
      header: "Port Profile",
      cell: info => {
        const val = info.getValue();
        const isProfileValid = val != null && val !== "" && val !== "No Profile";
        return <Box>{isProfileValid ? val : "-"}</Box>;
      },
    }),
    columnHelper.accessor(row => row.formattedFinalSpeedLabel, {
      id: "linkSpeed",
      header: "Port Speed",
      cell: info => <>{info.getValue()}</>,
    }),
    columnHelper.accessor(row => row.formattedPortTypeLabel, {
      id: "portType",
      header: "Port Type",
      cell: info => <>{info.getValue()}</>,
    }),
    columnHelper.accessor(row => {
      const opType = row?.operation?.type;
      const groupId = row?.operation?.aggregateGroupId;
      if (opType === "aggregation") return groupId != null ? `Aggregate #${groupId}` : "Aggregate";
      return opType ? (opType.charAt(0).toUpperCase() + opType.slice(1)) : "-";
    }, {
      id: "operation",
      header: "Operation",
      cell: info => <>{info.getValue() || "-"}</>,
    }),
    columnHelper.accessor(row => row.PVID ?? "-", {
      id: "PVID",
      header: "PVID",
      cell: info => <>{info.getValue()}</>,
    }),
    columnHelper.accessor(row => {
      const allowVlan = row?.allowVlan;
      if (Array.isArray(allowVlan)) {
        return allowVlan.length === 0 ? "-" : allowVlan.join(", ");
      }
      return allowVlan ?? "-";
    }, {
      id: "allowVlan",
      header: "Allowed VLANs",
      cell: info => <>{info.getValue()}</>,
    }),
    columnHelper.accessor(row => row.formattedRSTPLabel, {
      id: "RSTP",
      header: "RSTP",
      cell: info => <>{info.getValue()}</>,
    }),
    columnHelper.accessor(row => row.formattedLBDStateLabel, {
      id: "LBD",
      header: "LBD",
      cell: info => <>{info.getValue()}</>,
    }),
    columnHelper.accessor(row => row.formattedDDPStateLabel, {
      id: "DDP",
      header: "DDP",
      cell: info => <>{info.getValue()}</>,
    }),
    columnHelper.accessor(row => row.stpGuard ?? "-", {
      id: "stpGuard",
      header: "STP Guard",
      cell: (info) => <Box>{info.getValue()}</Box>,
    }),
    columnHelper.accessor(row => row.portSchedule ?? "-", {
      id: "portSchedule",
      header: "Port Schedule",
      cell: (info) => {
        const value = info.getValue();
        const matched = scheduleProfiles.find(opt => opt.value === value);
        return <Box>{matched ? matched.label : (value || "-")}</Box>;
      },
    }),
    columnHelper.accessor(row => row.poeSchedule ?? "-", {
      id: "poeSchedule",
      header: "PoE Schedule",
      cell: (info) => {
        const value = info.getValue();
        const matched = scheduleProfiles.find(opt => opt.value === value);
        return <Box>{matched ? matched.label : (value || "-")}</Box>;
      },
    }),
    columnHelper.accessor(row => row.accessPolicies ?? "-", {
      id: "accessPolicies",
      header: "Access Policies",
      cell: (info) => <Box>{info.getValue()}</Box>,
    }),
    columnHelper.accessor(row => row.formattedPortTagsLabel, {
      id: "portTags",
      header: "Tags",
      cell: info => <>{info.getValue()}</>,
    }),
  ].filter(Boolean);
}